// =============================================================================
// Solar.js - Dashboard de Energia Solar com Diagrama de Fluxo
// =============================================================================
// Smart Home - Monitorização Sungrow iSolarCloud
// =============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Sun, Battery, Home, Zap, ArrowLeft, RefreshCw, 
  TrendingUp, Leaf, Euro, ThermometerSun, Activity,
  ArrowRight, ArrowDown, ArrowUp, Clock, Calendar
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import './Solar.css';

const Solar = ({ apiUrl, onBack }) => {
  const { t, i18n } = useTranslation();
  const loc = () => (i18n.language === 'en' ? 'en-GB' : 'pt-PT');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  // Função para formatar potência
  const formatPower = (watts) => {
    if (watts === null || watts === undefined) return '0 W';
    if (Math.abs(watts) >= 1000) {
      return `${(watts / 1000).toFixed(2)} kW`;
    }
    return `${Math.round(watts)} W`;
  };

  // Função para formatar energia
  const formatEnergy = (kwh) => {
    if (kwh === null || kwh === undefined) return '0 kWh';
    if (kwh >= 1000) {
      return `${(kwh / 1000).toFixed(2)} MWh`;
    }
    return `${kwh.toFixed(1)} kWh`;
  };

  // Buscar dados
  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${apiUrl}/sungrow/status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Erro ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (result.success) {
        setData(result);
        setError(null);
        setLastUpdate(new Date());
      } else {
        throw new Error(result.error || t('solar.erroObter'));
      }
    } catch (err) {
      console.error('Erro ao buscar dados solar:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  // Carregar dados iniciais
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh a cada 30 segundos
  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchData, 30000);
    }
    return () => clearInterval(interval);
  }, [autoRefresh, fetchData]);

  // Determinar direcções do fluxo
  const getFlowDirections = () => {
    if (!data?.power_flow) return {};
    
    const pf = data.power_flow;
    return {
      pvToHome: pf.pv_power > 0 && pf.load_power > 0,
      pvToBattery: pf.battery_charge > 0,
      pvToGrid: pf.feed_in_power > 0,
      batteryToHome: pf.battery_discharge > 0,
      gridToHome: pf.purchase_power > 0,
    };
  };

  const flows = getFlowDirections();

  if (loading) {
    return (
      <div className="solar-container">
        <div className="solar-loading">
          <Sun className="spin" size={64} />
          <p>{t('solar.aCarregar')}</p>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="solar-container">
        <div className="solar-header">
          <button className="back-button" onClick={onBack}>
            <ArrowLeft size={24} />
            <span>{t('comum.voltar')}</span>
          </button>
          <h1><Sun size={32} /> {t('solar.titulo')}</h1>
        </div>
        <div className="solar-error">
          <p>❌ {error}</p>
          <button onClick={fetchData} className="retry-button">
            <RefreshCw size={20} /> {t('solar.tentarNovamente')}
          </button>
        </div>
      </div>
    );
  }

  const powerFlow = data?.power_flow || {};
  const realtime = data?.realtime || {};
  const plant = data?.plant || {};

  return (
    <div className="solar-container">
      {/* Header */}
      <div className="solar-header">
        <button className="back-button" onClick={onBack}>
          <ArrowLeft size={24} />
          <span>{t('comum.voltar')}</span>
        </button>
        <h1><Sun size={32} /> {plant.name || t('solar.titulo')}</h1>
        <div className="header-actions">
          <label className="auto-refresh-toggle">
            <input 
              type="checkbox" 
              checked={autoRefresh} 
              onChange={(e) => setAutoRefresh(e.target.checked)} 
            />
            <span>{t('solar.auto')}</span>
          </label>
          <button 
            className={`refresh-button ${loading ? 'spinning' : ''}`} 
            onClick={fetchData}
            disabled={loading}
          >
            <RefreshCw size={20} />
          </button>
        </div>
      </div>

      {/* Status Bar */}
      <div className="solar-status-bar">
        <div className="status-item">
          <Activity size={16} />
          <span className={`status-indicator ${plant.status === 1 ? 'online' : 'offline'}`}>
            {plant.status === 1 ? t('comum.online') : t('comum.offline')}
          </span>
        </div>
        <div className="status-item">
          <Zap size={16} />
          <span>{plant.capacity} {plant.capacity_unit}</span>
        </div>
        {lastUpdate && (
          <div className="status-item">
            <Clock size={16} />
            <span>{lastUpdate.toLocaleTimeString(loc())}</span>
          </div>
        )}
      </div>

      {/* Diagrama de Fluxo de Energia */}
      <div className="power-flow-diagram">
        <h2>{t('solar.fluxoTempoReal')}</h2>
        
        <div className="flow-grid">
          {/* Painéis Solares (Topo Centro) */}
          <div className="flow-node solar-node">
            <div className="node-icon solar-icon">
              <Sun size={48} />
            </div>
            <div className="node-value">{formatPower(powerFlow.pv_power)}</div>
            <div className="node-label">{t('solar.producaoSolar')}</div>
          </div>

          {/* Linha PV -> Casa */}
          <div className={`flow-line pv-to-home ${flows.pvToHome ? 'active' : ''}`}>
            <div className="flow-arrow">
              <ArrowDown size={24} />
            </div>
            {flows.pvToHome && (
              <div className="flow-value">
                {formatPower(Math.min(powerFlow.pv_power, powerFlow.load_power))}
              </div>
            )}
          </div>

          {/* Casa (Centro) */}
          <div className="flow-node home-node">
            <div className="node-icon home-icon">
              <Home size={48} />
            </div>
            <div className="node-value">{formatPower(powerFlow.load_power)}</div>
            <div className="node-label">{t('solar.consumoCasa')}</div>
          </div>

          {/* Bateria (Esquerda) */}
          <div className="flow-node battery-node">
            <div className="node-icon battery-icon">
              <Battery size={48} />
              <div className="battery-soc">{powerFlow.battery_soc?.toFixed(0)}%</div>
            </div>
            <div className="node-value">
              {powerFlow.battery_charge > 0 
                ? `+${formatPower(powerFlow.battery_charge)}`
                : powerFlow.battery_discharge > 0 
                  ? `-${formatPower(powerFlow.battery_discharge)}`
                  : '0 W'
              }
            </div>
            <div className="node-label">{t('solar.bateria')}</div>
            <div className="battery-bar">
              <div 
                className="battery-fill" 
                style={{ width: `${powerFlow.battery_soc || 0}%` }}
              ></div>
            </div>
          </div>

          {/* Linha Bateria <-> Casa */}
          <div className={`flow-line battery-home ${flows.batteryToHome || flows.pvToBattery ? 'active' : ''}`}>
            <div className="flow-arrow">
              {flows.pvToBattery ? <ArrowLeft size={24} /> : <ArrowRight size={24} />}
            </div>
            {(flows.batteryToHome || flows.pvToBattery) && (
              <div className="flow-value">
                {formatPower(flows.pvToBattery ? powerFlow.battery_charge : powerFlow.battery_discharge)}
              </div>
            )}
          </div>

          {/* Rede (Direita) */}
          <div className="flow-node grid-node">
            <div className="node-icon grid-icon">
              <Zap size={48} />
            </div>
            <div className="node-value">
              {powerFlow.feed_in_power > 0 
                ? `-${formatPower(powerFlow.feed_in_power)}`
                : powerFlow.purchase_power > 0 
                  ? `+${formatPower(powerFlow.purchase_power)}`
                  : '0 W'
              }
            </div>
            <div className="node-label">{t('solar.redeEletrica')}</div>
          </div>

          {/* Linha Casa <-> Rede */}
          <div className={`flow-line home-grid ${flows.gridToHome || flows.pvToGrid ? 'active' : ''}`}>
            <div className="flow-arrow">
              {flows.pvToGrid ? <ArrowRight size={24} /> : <ArrowLeft size={24} />}
            </div>
            {(flows.gridToHome || flows.pvToGrid) && (
              <div className="flow-value">
                {formatPower(flows.pvToGrid ? powerFlow.feed_in_power : powerFlow.purchase_power)}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Cards de Estatísticas Diárias */}
      <div className="solar-stats-grid">
        <div className="stat-card production">
          <div className="stat-icon"><Sun size={24} /></div>
          <div className="stat-content">
            <div className="stat-label">{t('solar.producaoHoje')}</div>
            <div className="stat-value">{formatEnergy(realtime.daily_pv_energy)}</div>
          </div>
        </div>

        <div className="stat-card consumption">
          <div className="stat-icon"><Home size={24} /></div>
          <div className="stat-content">
            <div className="stat-label">{t('solar.consumoHoje')}</div>
            <div className="stat-value">{formatEnergy(realtime.daily_load_energy)}</div>
          </div>
        </div>

        <div className="stat-card export">
          <div className="stat-icon"><TrendingUp size={24} /></div>
          <div className="stat-content">
            <div className="stat-label">{t('solar.exportadoHoje')}</div>
            <div className="stat-value">{formatEnergy(realtime.daily_feed_in_energy)}</div>
          </div>
        </div>

        <div className="stat-card import">
          <div className="stat-icon"><Zap size={24} /></div>
          <div className="stat-content">
            <div className="stat-label">{t('solar.importadoHoje')}</div>
            <div className="stat-value">{formatEnergy(realtime.daily_purchase_energy)}</div>
          </div>
        </div>

        <div className="stat-card battery-charge">
          <div className="stat-icon"><Battery size={24} /></div>
          <div className="stat-content">
            <div className="stat-label">{t('solar.carregadoHoje')}</div>
            <div className="stat-value">{formatEnergy(realtime.daily_charge_energy)}</div>
          </div>
        </div>

        <div className="stat-card battery-discharge">
          <div className="stat-icon"><Battery size={24} /></div>
          <div className="stat-content">
            <div className="stat-label">{t('solar.descarregadoHoje')}</div>
            <div className="stat-value">{formatEnergy(realtime.daily_discharge_energy)}</div>
          </div>
        </div>
      </div>

      {/* Cards de Totais e Info */}
      <div className="solar-info-grid">
        <div className="info-card">
          <div className="info-icon"><Calendar size={24} /></div>
          <div className="info-content">
            <div className="info-label">{t('solar.energiaTotal')}</div>
            <div className="info-value">{formatEnergy(plant.total_energy)}</div>
          </div>
        </div>

        <div className="info-card">
          <div className="info-icon"><Euro size={24} /></div>
          <div className="info-content">
            <div className="info-label">{t('solar.receitaTotal')}</div>
            <div className="info-value">{plant.total_income?.toFixed(2)} €</div>
          </div>
        </div>

        <div className="info-card">
          <div className="info-icon"><Leaf size={24} /></div>
          <div className="info-content">
            <div className="info-label">{t('solar.co2Evitado')}</div>
            <div className="info-value">{plant.co2_reduction?.toFixed(0)} kg</div>
          </div>
        </div>

        <div className="info-card">
          <div className="info-icon"><ThermometerSun size={24} /></div>
          <div className="info-content">
            <div className="info-label">{t('solar.tempBateria')}</div>
            <div className="info-value">{realtime.battery_temp?.toFixed(1)}°C</div>
          </div>
        </div>
      </div>

      {/* Detalhes da Bateria */}
      {realtime.battery_soc !== undefined && (
        <div className="battery-details-card">
          <h3><Battery size={24} /> {t('solar.detalhesBateria')}</h3>
          <div className="battery-details-grid">
            <div className="battery-detail">
              <span className="detail-label">{t('solar.socCarga')}</span>
              <span className="detail-value">{realtime.battery_soc?.toFixed(0)}%</span>
            </div>
            <div className="battery-detail">
              <span className="detail-label">{t('solar.sohSaude')}</span>
              <span className="detail-value">{realtime.battery_soh?.toFixed(0)}%</span>
            </div>
            <div className="battery-detail">
              <span className="detail-label">{t('solar.tensao')}</span>
              <span className="detail-value">{realtime.battery_voltage?.toFixed(1)} V</span>
            </div>
            <div className="battery-detail">
              <span className="detail-label">{t('solar.corrente')}</span>
              <span className="detail-value">{realtime.battery_current?.toFixed(1)} A</span>
            </div>
            <div className="battery-detail">
              <span className="detail-label">{t('solar.temperatura')}</span>
              <span className="detail-value">{realtime.battery_temp?.toFixed(1)}°C</span>
            </div>
          </div>
          <div className="battery-large-bar">
            <div 
              className={`battery-large-fill ${realtime.battery_soc < 20 ? 'low' : realtime.battery_soc < 50 ? 'medium' : 'high'}`}
              style={{ width: `${realtime.battery_soc || 0}%` }}
            >
              <span>{realtime.battery_soc?.toFixed(0)}%</span>
            </div>
          </div>
        </div>
      )}

      {/* Info da Instalação */}
      {plant.location && (
        <div className="plant-info-card">
          <h3><Sun size={24} /> {t('solar.infoInstalacao')}</h3>
          <div className="plant-info-grid">
            <div className="plant-info-item">
              <span className="info-key">{t('solar.nome')}</span>
              <span className="info-val">{plant.name}</span>
            </div>
            <div className="plant-info-item">
              <span className="info-key">{t('solar.capacidade')}</span>
              <span className="info-val">{plant.capacity} {plant.capacity_unit}</span>
            </div>
            <div className="plant-info-item">
              <span className="info-key">{t('solar.localizacao')}</span>
              <span className="info-val">{plant.location}</span>
            </div>
            <div className="plant-info-item">
              <span className="info-key">{t('solar.dataInstalacao')}</span>
              <span className="info-val">{plant.install_date?.split(' ')[0]}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Solar;
